<template>
  <div class="fab fab-right-bottom">
    <a>
      <i>
        <UCSvgIcon
          name="fa6-solid-plus"
          width="16px"
          height="16px"
        />
      </i>

      <i>
        <UCSvgIcon
          name="fa6-solid-xmark"
          width="16px"
          height="16px"
        />
      </i>
    </a>

    <div class="fab-buttons fab-buttons-top">
      <!-- 1. Finish day -->
      <a
        v-if="canFinishDay"
        class="fab-label-button fab-close"
        @click="confirmFinishDay()"
      >
        <UCSvgIcon
          name="fa6-solid-hourglass-end"
          width="16px"
          height="16px"
        />
        <span class="fab-label">Finalizar día</span>
      </a>

      <!-- 2. Tick -->
      <a
        class="fab-label-button fab-close"
        @click="isSelectWorkPositionPopupOpened = true"
      >
        <UCSvgIcon
          name="fa6-solid-ticket"
          width="16px"
          height="16px"
        />
        <span class="fab-label">Tickar</span>
      </a>
    </div>

    <!-- Popup: Select work position -->
    <PCSelectWorkPositionPopup
      v-model:opened="isSelectWorkPositionPopupOpened"
      @apply="registerTickAction"
    />
  </div>
</template>

<script setup>
// @ts-check
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRepairParts } from '@/composables/useRepairParts';
import { useRepairPartsStore } from '@/js/stores/repairParts';

import { logError } from '@/js/utils/log';
import { ConfirmDialogGH, HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import { HiDateTimeSupport } from '@/js/common/HIConversionSupport';
import {
  callCheckWorkerDayCompletion,
  callFinishLatestTickAction,
  callRegisterTickAction,
} from '@/js/services/repairs/ApiRepairs';

import UCSvgIcon from '@/components/common/UCSvgIcon.vue';
import PCSelectWorkPositionPopup from '../PCSelectWorkPositionPopup.vue';

/** @import { Ref } from 'vue' */
/** @import { PuestoTrabajo } from '@erpagroapp/types' */

const emit = defineEmits(['tickClicked']);

/** @type {Ref<boolean>} */
const isSelectWorkPositionPopupOpened = ref(false);

const { worker } = storeToRefs(useRepairPartsStore());

function useTick() {
  /**
   * Registers a tick action.
   * @param {PuestoTrabajo} workPosition - The work position object.
   */
  async function registerTickAction(workPosition) {
    if (!worker.value) {
      return;
    }

    HiDialog.Preloader(true, 'Registrando el tickaje...');

    try {
      const { data } = await callRegisterTickAction(
        worker.value.Id,
        workPosition.Id,
      );

      if (data.Valor === true) {
        HiNotification.Toast('Se ha tickado correctamente');
        emit('tickClicked');
      } else {
        HiNotification.Toast(data.Mensaje || 'No se ha podido registrar el tickaje');
      }
    } catch (error) {
      logError(error);
      HiNotification.Toast('No se ha podido registrar el tickaje');
    } finally {
      HiDialog.Preloader(false);
      isSelectWorkPositionPopupOpened.value = false;
    }
  }

  return {
    registerTickAction,
  };
}

function useFinishDay() {
  const canFinishDay = ref(false);

  const { selectedDate } = useRepairParts();

  /** Checks whether the worker can finish the day. */
  async function checkWorkerDayCompletion() {
    if (!worker.value) {
      return;
    }

    try {
      const { data } = await callCheckWorkerDayCompletion(worker.value.Id);
      canFinishDay.value = !!data.Valor;
    } catch (error) {
      logError(error);
    }
  }

  /** A method that is called when user confirmed finishing day */
  async function finishDay() {
    if (!worker.value) {
      return;
    }

    HiDialog.Preloader(true, 'Finalizando el día...');

    try {
      const { data } = await callFinishLatestTickAction(worker.value.Id);

      if (data.Valor === true) {
        canFinishDay.value = false;
        HiNotification.Toast('El día ha sido finalizado');
      } else {
        HiNotification.Toast(data.Mensaje || 'No se ha podido finalizar el día');
      }
    } catch (error) {
      logError(error);
      HiNotification.Toast('No se ha podido finalizar el día');
    } finally {
      HiDialog.Preloader(false);
    }
  }

  /**
   * Formats date to the DD/MM/YYYY format.
   * @param {Date} date
   */
  function formatDate(date) {
    return HiDateTimeSupport.FormatDateTime(date, 'DD/MM/YYYY');
  }

  /** It's called when user clicks on the 'Finish Day' option in the FAB. */
  async function confirmFinishDay() {
    const currentDate = formatDate(new Date());
    if (!selectedDate.value || formatDate(selectedDate.value.Init) !== currentDate) {
      HiNotification.Toast('No se puede finalizar el día, el día seleccionado no es hoy');
      return;
    }

    const resultConfirm = await ConfirmDialogGH(
      'Esta acción finalizará el día',
      '¿Quiere continuar?',
    );

    if (resultConfirm) {
      finishDay();
    }
  }

  watch(worker, () => {
    checkWorkerDayCompletion();
  }, { once: true });

  onMounted(() => {
    checkWorkerDayCompletion();
  });

  return {
    canFinishDay,
    confirmFinishDay,
  };
}

const { registerTickAction } = useTick();
const { canFinishDay, confirmFinishDay } = useFinishDay();
</script>
